import React, { useMemo, useState } from 'react';
import { logAuditEvent, logError } from '../services/Logger';
import {
  buildAssessmentExport,
  downloadCsv,
  makeSafeFilenamePart,
  SCHOOL_AUTHORIZATION_NOTICE,
  unparseAssessmentCsv,
} from '../utils/csv';

// TODO(sis-export): BEFORE re-enabling this component in production (flag
// `appConfig/featureFlags.assessmentCsvExport`) two things must be addressed:
//
//   1. The on-screen caption below claims the export contains "summary score
//      fields only, not item-level responses, timing details, PINs, parent/
//      contact data, or other sensitive fields." That is misleading: the
//      export DOES include `firstName`, `lastName`, and `localStudentId` for
//      every row — i.e. the student's full identity. It is NOT de-identified.
//      The caption must be corrected to disclose what is actually exported.
//
//   2. The full PII/field scope must be reviewed for the SIS-import use case
//      (InfiniteCampus / NextPath / others). Confirm which identifiers each
//      SIS actually needs vs what we're currently emitting, and whether the
//      audit log should also cover the Preview action (currently only
//      Download is logged). See BACKLOG.md → "SIS assessment export".
//
// The component itself is intentionally untouched while the feature is parked.

const PREVIEW_LIMIT = 10;

function AssessmentExportControls({
  user,
  students,
  classes,
  assessments,
  selectedClassFilter = 'All',
  classIds = [],
  filterAssessment,
  assessmentTypeLabel,
  defaultAssessmentCategory,
  filenamePrefix = 'Assessment_Results',
}) {
  const [showPreview, setShowPreview] = useState(false);

  const visibleClassIds = useMemo(() => new Set(classIds), [classIds]);

  const exportAssessments = useMemo(() => {
    return (assessments || []).filter(assessment => {
      if (visibleClassIds.size > 0 && !visibleClassIds.has(assessment.classId)) return false;
      return filterAssessment ? filterAssessment(assessment) : true;
    });
  }, [assessments, filterAssessment, visibleClassIds]);

  const exportData = useMemo(() => buildAssessmentExport({
    students,
    classes,
    assessments: exportAssessments,
    assessmentTypeLabel,
    defaultAssessmentCategory,
  }), [assessmentTypeLabel, classes, defaultAssessmentCategory, exportAssessments, students]);

  const handleDownload = async () => {
    if (exportData.rowCount === 0) {
      alert('No assessment rows are available to export with the current filters.');
      return;
    }

    try {
      const csvContent = await unparseAssessmentCsv(exportData);
      const classPart = selectedClassFilter === 'All' ? 'All_Classes' : makeSafeFilenamePart(selectedClassFilter.split('_')[0]);
      downloadCsv(`${makeSafeFilenamePart(filenamePrefix)}_${classPart}.csv`, csvContent);
      await logAuditEvent(user, 'ASSESSMENTS_EXPORTED', selectedClassFilter, {
        rowCount: exportData.rowCount,
        fields: exportData.fields,
        classScope: selectedClassFilter,
      });
    } catch (err) {
      console.error('Assessment export error:', err);
      await logError(user, 'ASSESSMENT_EXPORT_ERROR', err.message, err.stack, {
        rowCount: exportData?.rowCount || 0,
      });
      alert('Could not export assessment results. Please try again.');
    }
  };

  return (
    <div className="no-print" style={{ maxWidth: '980px', margin: '0 auto 20px auto', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '12px', background: '#f8fafc' }}>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setShowPreview(prev => !prev)}
          style={{ background: '#475569', color: 'white', border: 'none', borderRadius: '4px', padding: '9px 14px', cursor: 'pointer' }}
        >
          {showPreview ? 'Hide Preview' : 'Preview Export'}
        </button>
        <button
          type="button"
          onClick={handleDownload}
          disabled={exportData.rowCount === 0}
          style={{
            background: exportData.rowCount === 0 ? '#94a3b8' : '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '9px 14px',
            cursor: exportData.rowCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          Download CSV
        </button>
      </div>
      <p style={{ margin: '8px 0 0 0', color: '#64748b', fontSize: '0.9em', textAlign: 'center' }}>
        {SCHOOL_AUTHORIZATION_NOTICE} Default assessment exports include summary score fields only, not item-level responses, timing details, PINs, parent/contact data, or other sensitive fields.
      </p>
      {showPreview && (
        <div style={{ marginTop: '12px', textAlign: 'left' }}>
          <strong>Export preview</strong>
          <p style={{ margin: '6px 0', color: '#475569' }}>
            {exportData.rowCount} row{exportData.rowCount === 1 ? '' : 's'} will export. Fields: {exportData.fields.join(', ')}.
          </p>
          {exportData.rowCount > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85em' }}>
                <thead>
                  <tr>
                    {exportData.fields.map(field => (
                      <th key={field} style={{ borderBottom: '1px solid #cbd5e1', padding: '6px', textAlign: 'left' }}>{field}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {exportData.rows.slice(0, PREVIEW_LIMIT).map((row, rowIndex) => (
                    <tr key={`assessment-export-${rowIndex}`}>
                      {exportData.fields.map(field => (
                        <td key={field} style={{ borderBottom: '1px solid #e2e8f0', padding: '6px' }}>{row[field] || ''}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {exportData.rowCount > PREVIEW_LIMIT && (
                <p style={{ margin: '8px 0 0 0', color: '#64748b', fontSize: '0.85em' }}>
                  Showing first {PREVIEW_LIMIT} rows.
                </p>
              )}
            </div>
          ) : (
            <p style={{ color: '#64748b', marginBottom: 0 }}>No rows match the current filters.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default AssessmentExportControls;
